import React, { useState, useEffect } from 'react'
import {
    Row,
    Col,
    Card,
    Statistic,
    Typography,
    Space,
    Table,
    Tag,
    Button,
    Spin,
    Alert,
    Progress,
    List,
    Avatar,
    Badge
} from 'antd'
import {
    DollarOutlined,
    ShoppingCartOutlined,
    FileTextOutlined,
    TeamOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    CalendarOutlined,
    AlertOutlined,
    EyeOutlined,
    CreditCardOutlined,
    CloudUploadOutlined,
    ClockCircleOutlined,
    WarningOutlined
} from '@ant-design/icons'
import { useQuery } from 'react-query'
import { Line } from '@ant-design/charts'
import dayjs from 'dayjs'
import api from '../../utils/api'
import { formatCurrency, formatDate, getStatusColor, getStatusText } from '../../utils/helpers'
import { useAuth } from '../../contexts/AuthContext'

const { Title, Text } = Typography

const Dashboard = () => {
    const { user, hasPermission } = useAuth()
    const [timeRange, setTimeRange] = useState('month')

    // Fetch dashboard data
    const { data: dashboardData, isLoading, error } = useQuery(
        ['dashboard', timeRange],
        () => api.get(`/api/reports/dashboard?period=${timeRange}`),
        {
            refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
            staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
        }
    )

    // Fetch low stock products if user has permission
    const { data: lowStockData } = useQuery(
        'low-stock',
        () => api.get('/api/products/reports/low-stock'),
        {
            enabled: hasPermission('products', 'read'),
            refetchInterval: 10 * 60 * 1000,
        }
    )

    // Fetch upcoming events if user has permission
    const { data: upcomingEvents } = useQuery(
        'upcoming-events',
        () => {
            // Get events for next 7 days with priority for today and tomorrow
            const today = dayjs().format('YYYY-MM-DD')
            const nextday = dayjs().add(3, 'days').format('YYYY-MM-DD')
            return api.get(`/api/calendar/upcoming/list?start_date>=${today}&end_date<=${nextday}&limit=10`)
        },
        {
            enabled: hasPermission('calendar', 'read'),
            refetchInterval: 5 * 60 * 1000, // More frequent refresh for urgent events
        }
    )

    // Helper function to get event urgency
    const getEventUrgency = (eventDate) => {
        const today = dayjs()
        const eventDay = dayjs(eventDate)
        const diffDays = eventDay.diff(today, 'days')

        if (diffDays === 0) return 'today'
        if (diffDays === 1) return 'tomorrow'
        if (diffDays <= 3) return 'soon'
        return 'later'
    }

    // Helper function to get urgency colors and text
    const getUrgencyStyle = (urgency) => {
        switch (urgency) {
            case 'today':
                return { color: '#ff4d4f', text: 'พรุ่งนี้!', icon: <WarningOutlined /> }
            case 'tomorrow':
                return { color: '#fa8c16', text: 'มะรืนนี้!', icon: <ClockCircleOutlined /> }
            case 'soon':
                return { color: '#fadb14', text: 'เร็วๆนี้', icon: <ClockCircleOutlined /> }
            default:
                return { color: '#1890ff', text: '', icon: null }
        }
    }

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <Spin size="large" tip="กำลังโหลดข้อมูล..." />
            </div>
        )
    }

    if (error) {
        return (
            <Alert
                message="เกิดข้อผิดพลาด"
                description="ไม่สามารถโหลดข้อมูลแดชบอร์ดได้ โปรดลองใหม่อีกครั้ง"
                type="error"
                showIcon
            />
        )
    }

    const summary = dashboardData?.data?.data?.summary || {}
    const recentActivities = dashboardData?.data?.data?.recent_activities || { receipts: [], expenses: [] }
    const events = upcomingEvents?.data?.data || []
    const lowStockProducts = lowStockData?.data?.data || []

    // Filter and sort events by urgency
    const sortedEvents = events
        .map(event => ({
            ...event,
            urgency: getEventUrgency(event.start_date)
        }))
        .sort((a, b) => {
            const urgencyOrder = { 'today': 0, 'tomorrow': 1, 'soon': 2, 'later': 3 }
            const aOrder = urgencyOrder[a.urgency] || 4
            const bOrder = urgencyOrder[b.urgency] || 4

            if (aOrder !== bOrder) return aOrder - bOrder
            return dayjs(a.start_date).diff(dayjs(b.start_date))
        })

    // Count urgent events (today + tomorrow)
    const urgentEventsCount = sortedEvents.filter(event =>
        event.urgency === 'today' || event.urgency === 'tomorrow'
    ).length

    // Statistics cards data
    const statsData = [
        {
            title: 'ยอดขายเดือนนี้',
            value: summary.total_sales || 0,
            prefix: <DollarOutlined style={{ color: '#52c41a' }} />,
            precision: 2,
            valueStyle: { color: '#52c41a' },
            formatter: (value) => formatCurrency(value),
            suffix: summary.sales_growth ? (
                <span style={{ fontSize: '14px', marginLeft: '8px' }}>
                    {summary.sales_growth > 0 ? (
                        <><ArrowUpOutlined style={{ color: '#52c41a' }} /> +{summary.sales_growth.toFixed(1)}%</>
                    ) : (
                        <><ArrowUpOutlined style={{ color: '#ff4d4f' }} /> {summary.sales_growth.toFixed(1)}%</>
                    )}
                </span>
            ) : null
        },
        {
            title: 'ใบเสร็จที่ชำระแล้ว',
            value: summary.paid_receipts || 0,
            prefix: <FileTextOutlined style={{ color: '#1890ff' }} />,
            valueStyle: { color: '#1890ff' }
        },
        {
            title: 'ยอดค้างชำระ',
            value: summary.outstanding_amount || 0,
            prefix: <FileTextOutlined style={{ color: '#faad14' }} />,
            precision: 2,
            valueStyle: { color: '#faad14' },
            formatter: (value) => formatCurrency(value)
        },
        {
            title: 'ค่าใช้จ่ายเดือนนี้',
            value: summary.total_expenses || 0,
            prefix: <CreditCardOutlined style={{ color: '#ff4d4f' }} />,
            precision: 2,
            valueStyle: { color: '#ff4d4f' },
            formatter: (value) => formatCurrency(value)
        },
        // ✅ เพิ่มมูลค่าใบเสนอราคาแยกตามสถานะ
        {
            title: 'ใบเสนอราคา (ร่าง)',
            value: summary.quotes_draft || 0,
            prefix: <FileTextOutlined style={{ color: '#8c8c8c' }} />,
            precision: 2,
            valueStyle: { color: '#8c8c8c' },
            formatter: (value) => formatCurrency(value)
        },
        {
            title: 'ใบเสนอราคา (ส่งแล้ว)',
            value: summary.quotes_sent || 0,
            prefix: <FileTextOutlined style={{ color: '#1890ff' }} />,
            precision: 2,
            valueStyle: { color: '#1890ff' },
            formatter: (value) => formatCurrency(value)
        },
        {
            title: 'ใบเสนอราคา (อนุมัติแล้ว)',
            value: summary.quotes_approved || 0,
            prefix: <FileTextOutlined style={{ color: '#52c41a' }} />,
            precision: 2,
            valueStyle: { color: '#52c41a' },
            formatter: (value) => formatCurrency(value)
        }
    ]

    // Recent receipts table columns
    const receiptColumns = [
        {
            title: 'เลขที่เอกสาร',
            dataIndex: 'invoice_number',
            key: 'invoice_number',
            render: (text, record) => (
                <span>
                    {record.receipt_number || record.invoice_number}
                </span>
            )
        },
        {
            title: 'ลูกค้า',
            dataIndex: 'customer_name',
            key: 'customer_name'
        },
        {
            title: 'ยอดเงิน',
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (amount) => formatCurrency(amount),
            align: 'right'
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusText(status)}
                </Tag>
            )
        }
    ]

    // Recent expenses table columns
    const expenseColumns = [
        {
            title: 'เลขที่ค่าใช้จ่าย',
            dataIndex: 'expense_number',
            key: 'expense_number'
        },
        {
            title: 'รายละเอียด',
            dataIndex: 'description',
            key: 'description',
            render: (text) => (
                <span title={text}>
                    {text.length > 30 ? `${text.substring(0, 30)}...` : text}
                </span>
            )
        },
        {
            title: 'ยอดเงิน',
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (amount) => formatCurrency(amount),
            align: 'right'
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusText(status)}
                </Tag>
            )
        }
    ]

    return (
        <div className="dashboard-container fade-in">
            {/* Header */}
            <div className="dashboard-header">
                <Title level={2} className="dashboard-title">
                    ยินดีต้อนรับ, {user?.name}
                </Title>
                <Text type="secondary" className="dashboard-subtitle">
                    ภาพรวมข้อมูลธุรกิจของคุณ - {formatDate(new Date(), 'DD MMMM YYYY')}
                </Text>
            </div>

            {/* Statistics Cards */}
            <Row gutter={[16, 16]} className="stats-row">
                {statsData.map((stat, index) => (
                    <Col xs={24} sm={12} lg={6} key={index}>
                        <Card className="stat-card" bordered={false}>
                            <Statistic
                                title={stat.title}
                                value={stat.value}
                                precision={stat.precision}
                                valueStyle={stat.valueStyle}
                                prefix={stat.prefix}
                                suffix={stat.suffix}
                                formatter={stat.formatter}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Content Row */}
            <Row gutter={[16, 16]}>
                {/* Recent Activities */}
                <Col xs={24} lg={16}>
                    <Row gutter={[16, 16]}>
                        {/* Recent Receipts */}
                        {hasPermission('receipts', 'read') && (
                            <Col xs={24}>
                                <Card
                                    title="ใบเสร็จล่าสุด"
                                    extra={<Button type="link" href="/receipts">ดูทั้งหมด</Button>}
                                    bordered={false}
                                >
                                    <Table
                                        columns={receiptColumns}
                                        dataSource={recentActivities.receipts?.slice(0, 5) || []}
                                        pagination={false}
                                        size="small"
                                        rowKey="id"
                                        locale={{ emptyText: 'ไม่มีข้อมูล' }}
                                    />
                                </Card>
                            </Col>
                        )}

                        {/* Recent Expenses */}
                        {hasPermission('expenses', 'read') && (
                            <Col xs={24}>
                                <Card
                                    title="ค่าใช้จ่ายล่าสุด"
                                    extra={<Button type="link" href="/expenses">ดูทั้งหมด</Button>}
                                    bordered={false}
                                >
                                    <Table
                                        columns={expenseColumns}
                                        dataSource={recentActivities.expenses?.slice(0, 5) || []}
                                        pagination={false}
                                        size="small"
                                        rowKey="id"
                                        locale={{ emptyText: 'ไม่มีข้อมูล' }}
                                    />
                                </Card>
                            </Col>
                        )}
                    </Row>
                </Col>

                {/* Sidebar */}
                <Col xs={24} lg={8}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {/* Low Stock Alert */}
                        {hasPermission('products', 'read') && lowStockProducts.length > 0 && (
                            <Card
                                title={(
                                    <Space>
                                        <AlertOutlined style={{ color: '#ff4d4f' }} />
                                        สินค้าใกล้หมด
                                    </Space>
                                )}
                                extra={<Button type="link" href="/products?low_stock=true" size="small">ดูทั้งหมด</Button>}
                                bordered={false}
                                size="small"
                            >
                                <List
                                    size="small"
                                    dataSource={lowStockProducts.slice(0, 5)}
                                    renderItem={(product) => (
                                        <List.Item>
                                            <List.Item.Meta
                                                title={
                                                    <Text style={{ fontSize: '13px' }}>
                                                        {product.name}
                                                    </Text>
                                                }
                                                description={
                                                    <Space size="small">
                                                        <Text type="danger" style={{ fontSize: '12px' }}>
                                                            คงเหลือ: {product.stock}
                                                        </Text>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            (ต่ำสุด: {product.min_stock})
                                                        </Text>
                                                    </Space>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                    locale={{ emptyText: 'ไม่มีสินค้าใกล้หมด' }}
                                />
                            </Card>
                        )}

                        {/* Urgent Events Alert */}
                        {hasPermission('calendar', 'read') && (
                            <Card
                                title={(
                                    <Space>
                                        {/* <Badge count={urgentEventsCount} offset={[10, 0]}>
                      <WarningOutlined style={{ color: urgentEventsCount > 0 ? '#ff4d4f' : '#1890ff' }} />
                    </Badge> */}
                                        <span style={{ color: urgentEventsCount > 0 ? '#ff4d4f' : 'inherit' }}>
                                            กำหนดการเร่งด่วน
                                        </span>
                                    </Space>
                                )}
                                extra={<Button type="link" href="/calendar" size="small">ดูทั้งหมด</Button>}
                                bordered={false}
                                size="small"
                            >
                                <List
                                    size="small"
                                    dataSource={sortedEvents.slice(0, 6)}
                                    renderItem={(event) => {
                                        const urgencyStyle = getUrgencyStyle(event.urgency)
                                        return (
                                            <List.Item style={{
                                                backgroundColor: event.urgency === 'today' ? '#fff2f0' :
                                                    event.urgency === 'tomorrow' ? '#fff7e6' : 'transparent',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                marginBottom: '4px',
                                                border: event.urgency === 'today' ? '1px solid #ffccc7' :
                                                    event.urgency === 'tomorrow' ? '1px solid #ffd591' : 'none'
                                            }}>
                                                <List.Item.Meta
                                                    avatar={
                                                        <Avatar
                                                            size="small"
                                                            style={{
                                                                backgroundColor: event.color || urgencyStyle.color,
                                                                border: event.urgency === 'today' ? '2px solid #ff4d4f' : 'none'
                                                            }}
                                                        >
                                                            {urgencyStyle.icon || event.title.charAt(0)}
                                                        </Avatar>
                                                    }
                                                    title={
                                                        <Space>
                                                            <Text style={{
                                                                fontSize: '13px',
                                                                fontWeight: event.urgency === 'today' || event.urgency === 'tomorrow' ? 'bold' : 'normal'
                                                            }}>
                                                                {event.title}
                                                            </Text>
                                                            {urgencyStyle.text && (
                                                                <Tag
                                                                    color={urgencyStyle.color}
                                                                    size="small"
                                                                    style={{
                                                                        fontSize: '10px',
                                                                        lineHeight: '16px',
                                                                        marginLeft: '4px'
                                                                    }}
                                                                >
                                                                    {urgencyStyle.text}
                                                                </Tag>
                                                            )}
                                                        </Space>
                                                    }
                                                    description={
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            {formatDate(event.start_date, 'DD/MM/YYYY')}
                                                            {event.start_time && ` ${event.start_time}`}
                                                            {event.urgency === 'today' && (
                                                                <Text style={{ color: '#ff4d4f', marginLeft: '8px', fontSize: '11px' }}>
                                                                    ⚠️ วันพรุ่งนี้!
                                                                </Text>
                                                            )}
                                                            {event.urgency === 'tomorrow' && (
                                                                <Text style={{ color: '#fa8c16', marginLeft: '8px', fontSize: '11px' }}>
                                                                    ⏰ เตรียมตัว
                                                                </Text>
                                                            )}
                                                        </Text>
                                                    }
                                                />
                                            </List.Item>
                                        )
                                    }}
                                    locale={{ emptyText: 'ไม่มีกำหนดการที่จะมาถึง' }}
                                />

                                {/* Summary footer for urgent events */}
                                {urgentEventsCount > 0 && (
                                    <div style={{
                                        marginTop: '12px',
                                        padding: '8px',
                                        backgroundColor: '#fff2f0',
                                        borderRadius: '4px',
                                        border: '1px solid #ffccc7'
                                    }}>
                                        <Text style={{ fontSize: '12px', color: '#ff4d4f', fontWeight: 'bold' }}>
                                            🔔 คุณมีกำหนดการเร่งด่วน {urgentEventsCount} รายการ ที่ต้องเตรียมตัว!
                                        </Text>
                                    </div>
                                )}
                            </Card>
                        )}

                        {/* Quick Actions */}
                        {user.role==='SUPER_ADMIN' && (
                            <>
                            <Card
                            title="เมนูด่วน"
                            bordered={false}
                            size="small"
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                {hasPermission('receipts', 'create') && (
                                    <Button
                                        type="primary"
                                        block
                                        href="/receipts/new"
                                        icon={<FileTextOutlined />}
                                    >
                                        สร้างใบเสร็จ
                                    </Button>
                                )}
                                {hasPermission('expenses', 'create') && (
                                    <Button
                                        block
                                        href="/expenses/new"
                                        icon={<CreditCardOutlined />}
                                    >
                                        บันทึกค่าใช้จ่าย
                                    </Button>
                                )}
                                {hasPermission('products', 'read') && (
                                    <Button
                                        block
                                        href="/products"
                                        icon={<ShoppingCartOutlined />}
                                    >
                                        จัดการสินค้า
                                    </Button>
                                )}
                                {/* <Button
                                    block
                                    href="/upload"
                                    icon={<CloudUploadOutlined />}
                                >
                                    อัปโหลดเอกสาร
                                </Button> */}
                            </Space>
                        </Card>
                            
                            </>
                        )}
                        
                    </Space>
                </Col>
            </Row>
        </div>
    )
}

export default Dashboard